import { useCallback, useEffect, useRef, useState } from "react";

export interface ResizableSidePanelConfig {
  /** Minimum panel width in px; also the floor for the persisted width. */
  readonly minPx: number;
  /** Width used before any drag and when persistence is empty/invalid. */
  readonly defaultPx: number;
  /** localStorage key the panel width is persisted under. */
  readonly storageKey: string;
  /** Whether the panel is currently open — drives the slide-in animation. */
  readonly isOpen: boolean;
}

export interface ResizableSidePanel {
  /** Width to render the panel at (live drag width, else capped persisted). */
  readonly currentWidth: number;
  /** True mid-drag — callers disable CSS transitions while it's set. */
  readonly isDragging: boolean;
  /** True once opened at least once — gates mounting + the pushed margin. */
  readonly hasBeenOpened: boolean;
  /** True when the panel should sit on-screen (translateX(0)). */
  readonly panelVisible: boolean;
  readonly handleMouseDown: (e: React.MouseEvent) => void;
  readonly handleTouchStart: (e: React.TouchEvent) => void;
  /** Arrow-key resize for the focusable (`<button>`) drag handle. */
  readonly handleKeyDown: (e: React.KeyboardEvent) => void;
}

/** Width step (px) applied per Arrow keypress on the resize handle. */
const KEYBOARD_RESIZE_STEP_PX = 24;

function readPersistedWidth(
  storageKey: string,
  minPx: number,
  defaultPx: number,
): number {
  if (globalThis.window === undefined) return defaultPx;
  // localStorage access can throw (private browsing, disabled storage, CSP).
  try {
    const raw = globalThis.localStorage.getItem(storageKey);
    if (!raw) return defaultPx;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < minPx) return defaultPx;
    return parsed;
  } catch {
    return defaultPx;
  }
}

function persistWidth(storageKey: string, width: number): void {
  try {
    globalThis.localStorage.setItem(storageKey, String(width));
  } catch {
    // Width persistence is best-effort; a storage failure must not break drag.
  }
}

function resetBodyDragStyles(): void {
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
}

/**
 * Page-pushing resizable side-panel mechanics shared by the recap workspace
 * ChatPanel and the AI source-document panel: drag-to-resize (mouse + touch)
 * with a localStorage-persisted width, plus the double-rAF slide-in animation.
 *
 * Behaviour is identical across both panels; only the size constants, storage
 * key, and open signal differ, so those come in via `config`. Callers own the
 * surrounding markup (drag handle, `fixed` panel, margin push) and read
 * `currentWidth` / `isDragging` / `hasBeenOpened` / `panelVisible` from here.
 */
export function useResizableSidePanel({
  minPx,
  defaultPx,
  storageKey,
  isOpen,
}: ResizableSidePanelConfig): ResizableSidePanel {
  const [panelWidth, setPanelWidth] = useState<number>(() =>
    readPersistedWidth(storageKey, minPx, defaultPx),
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const isDragging = dragWidth !== null;

  // Teardown for the in-flight drag (detach global listeners + reset body
  // styles). Held in a ref so the unmount effect can run it if the component
  // unmounts mid-drag, which would otherwise orphan listeners + sticky styles.
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const cappedPanelWidth =
    globalThis.window === undefined
      ? panelWidth
      : Math.max(minPx, Math.min(globalThis.innerWidth, panelWidth));

  const startDrag = useCallback(
    (startX: number) => {
      const startWidth = cappedPanelWidth;
      const maxWidth = globalThis.innerWidth;
      const onMove = (clientX: number) => {
        const delta = startX - clientX;
        setDragWidth(Math.max(minPx, Math.min(maxWidth, startWidth + delta)));
      };
      const onEnd = (clientX: number) => {
        const delta = startX - clientX;
        const finalWidth = Math.max(
          minPx,
          Math.min(maxWidth, startWidth + delta),
        );
        setPanelWidth(finalWidth);
        persistWidth(storageKey, finalWidth);
        setDragWidth(null);
      };
      return { onMove, onEnd };
    },
    [cappedPanelWidth, minPx, storageKey],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const { onMove, onEnd } = startDrag(e.clientX);
      const onMouseMove = (ev: MouseEvent) => onMove(ev.clientX);
      const detach = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        resetBodyDragStyles();
        dragCleanupRef.current = null;
      };
      const onMouseUp = (ev: MouseEvent) => {
        detach();
        onEnd(ev.clientX);
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      dragCleanupRef.current = detach;
    },
    [startDrag],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      const { onMove, onEnd } = startDrag(touch.clientX);
      const detach = () => {
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
        document.removeEventListener("touchcancel", onTouchCancel);
        resetBodyDragStyles();
        dragCleanupRef.current = null;
      };
      function onTouchMove(ev: TouchEvent) {
        ev.preventDefault();
        onMove(ev.touches[0].clientX);
      }
      function onTouchEnd(ev: TouchEvent) {
        detach();
        onEnd(ev.changedTouches[0].clientX);
      }
      function onTouchCancel() {
        detach();
        onEnd(touch.clientX);
      }
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
      document.addEventListener("touchcancel", onTouchCancel);
      dragCleanupRef.current = detach;
    },
    [startDrag],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // The panel hugs the right edge and grows leftward, so ArrowLeft widens
      // and ArrowRight narrows it.
      let next: number | null = null;
      if (e.key === "ArrowLeft")
        next = cappedPanelWidth + KEYBOARD_RESIZE_STEP_PX;
      else if (e.key === "ArrowRight")
        next = cappedPanelWidth - KEYBOARD_RESIZE_STEP_PX;
      if (next === null) return;
      e.preventDefault();
      const clamped = Math.max(minPx, Math.min(globalThis.innerWidth, next));
      setPanelWidth(clamped);
      persistWidth(storageKey, clamped);
    },
    [cappedPanelWidth, minPx, storageKey],
  );

  // Tear down an interrupted drag if the component unmounts mid-gesture.
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const currentWidth = dragWidth ?? cappedPanelWidth;

  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setPanelVisible(false);
      return;
    }
    setHasBeenOpened(true);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPanelVisible(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [isOpen]);

  return {
    currentWidth,
    isDragging,
    hasBeenOpened,
    panelVisible,
    handleMouseDown,
    handleTouchStart,
    handleKeyDown,
  };
}
